import { useId } from "react";
import { Controller, useFormContext, type FieldPath, type FieldValues } from "react-hook-form";

import { Checkbox, type CheckboxProps } from "../../atoms/checkbox";
import type { FormFieldBaseProps } from "./types";

export type FormCheckboxProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Pick<FormFieldBaseProps<TFieldValues, TName>, "name"> &
  Omit<CheckboxProps, "checked" | "onCheckedChange"> & {
    /** Called with the new value after the form's value changes */
    onCheckedChange?: CheckboxProps["onCheckedChange"];
  };

export const FormCheckbox = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  onCheckedChange,
  ...checkboxProps
}: FormCheckboxProps<TFieldValues, TName>) => {
  const id = useId();
  const { control } = useFormContext<TFieldValues>();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Checkbox
          {...checkboxProps}
          checked={!!field.value}
          onCheckedChange={(checked) => {
            field.onChange(checked);
            onCheckedChange?.(checked);
          }}
          testID={checkboxProps.testID ?? id}
        />
      )}
    />
  );
};

FormCheckbox.displayName = "FormCheckbox";
